import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Globe, 
  ArrowLeftRight, 
  Loader2, 
  Copy, 
  Check,
  Lock,
  Sparkles,
  ChevronDown,
  AlertTriangle,
  Upload,
  FileText,
  FileDown,
  X,
  LogOut,
  User,
  Zap,
  Database
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TranslatePage = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [sourceText, setSourceText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [sourceLang, setSourceLang] = useState("en-ca");
  const [targetLang, setTargetLang] = useState("fr-ca");
  const [selectedModel, setSelectedModel] = useState("gpt-5.2"); // Default to highest accuracy
  const [alertPriority, setAlertPriority] = useState("normal");
  const [isTranslating, setIsTranslating] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [samples, setSamples] = useState([]);
  
  // Translation progress state
  const [translationStatus, setTranslationStatus] = useState("");
  const [translationProgress, setTranslationProgress] = useState(0);
  
  // Live alerts state
  const [useLiveAlerts, setUseLiveAlerts] = useState(true);
  const [liveAlertsInfo, setLiveAlertsInfo] = useState(null);
  const [isRefreshingAlerts, setIsRefreshingAlerts] = useState(false);
  
  // Batch upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const fileInputRef = useRef(null);
  
  // Terminology highlighting state
  const [showTermHighlights, setShowTermHighlights] = useState(true);

  const models = [
    { id: "gpt-5.2", name: "GPT-5.2", description: "Highest accuracy (recommended)" },
    { id: "claude-sonnet-4-5", name: "Claude Sonnet 4.5", description: "Strong reasoning" },
    { id: "gemini-3-flash", name: "Gemini 3 Flash", description: "Fastest" },
    { id: "auto", name: "Auto (Smart Routing)", description: "Balanced routing" },
  ];

  const priorities = [
    { id: "critical", name: "Critical", color: "text-red-500" },
    { id: "high", name: "High", color: "text-secondary" },
    { id: "normal", name: "Normal", color: "text-muted-foreground" },
    { id: "batch", name: "Batch", color: "text-cyan-600" },
  ];

  // Function to highlight locked terminology in text
  const highlightTerminology = (text, terminologyUsed, isSource = false) => {
    if (!text || !terminologyUsed || terminologyUsed.length === 0 || !showTermHighlights) {
      return text;
    }

    // Build an array of segments with highlighted terms
    let segments = [{ text, isHighlight: false }];
    
    // Sort terms by length (longest first) to avoid partial replacements
    const sortedTerms = [...terminologyUsed].sort((a, b) => 
      (isSource ? (b.original?.length || 0) : (b.translation?.length || 0)) - 
      (isSource ? (a.original?.length || 0) : (a.translation?.length || 0))
    );

    sortedTerms.forEach((term) => {
      const searchTerm = isSource ? term.original : term.translation;
      if (!searchTerm) return;
      
      const newSegments = [];
      
      segments.forEach(segment => {
        if (segment.isHighlight) {
          newSegments.push(segment);
          return;
        }
        
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = segment.text.split(regex);
        
        parts.forEach((part, i) => {
          if (part.toLowerCase() === searchTerm.toLowerCase()) {
            newSegments.push({
              text: part,
              isHighlight: true,
              term: {
                original: term.original,
                translation: term.translation,
                locked: term.locked
              }
            });
          } else if (part) {
            newSegments.push({ text: part, isHighlight: false });
          }
        });
      });
      
      segments = newSegments;
    });

    // Convert to React elements
    return segments.map((segment, index) => {
      if (segment.isHighlight) {
        return (
          <span
            key={`term-${index}`}
            className="terminology-highlight group relative inline"
            title={`Locked: ${segment.term.original} ↔ ${segment.term.translation}`}
          >
            <span className="bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded border border-emerald-300 font-medium">
              {segment.text}
              <Lock className="inline w-3 h-3 ml-1 text-emerald-600" />
            </span>
          </span>
        );
      }
      return segment.text;
    });
  };

  useEffect(() => {
    fetchSamples();
  }, [sourceLang, useLiveAlerts]);

  const fetchSamples = async () => {
    try {
      if (useLiveAlerts) {
        // Fetch live alerts from ECCC
        const response = await axios.get(`${API}/alerts/live?lang=${sourceLang}`);
        const liveAlerts = response.data.alerts.map(alert => ({
          title: alert.title,
          text: alert.text,
          source: alert.source
        }));
        setSamples(liveAlerts);
        setLiveAlertsInfo({
          lastUpdated: response.data.last_updated,
          nextUpdate: response.data.next_update,
          alertCount: response.data.alert_count,
          isDemo: response.data.is_demo || false,
          source: response.data.source
        });
      } else {
        // Fetch static sample alerts
        const response = await axios.get(`${API}/samples?lang=${sourceLang}`);
        setSamples(response.data);
        setLiveAlertsInfo(null);
      }
    } catch (error) {
      console.error("Failed to fetch samples:", error);
      // Fallback to static samples if live alerts fail
      if (useLiveAlerts) {
        try {
          const response = await axios.get(`${API}/samples?lang=${sourceLang}`);
          setSamples(response.data);
          toast.error("Live alerts unavailable, showing sample alerts");
        } catch (e) {
          console.error("Failed to fetch fallback samples:", e);
        }
      }
    }
  };

  const refreshLiveAlerts = async () => {
    setIsRefreshingAlerts(true);
    try {
      await axios.post(`${API}/alerts/refresh`);
      await fetchSamples();
      toast.success("Live alerts refreshed from ECCC");
    } catch (error) {
      toast.error("Failed to refresh live alerts");
    } finally {
      setIsRefreshingAlerts(false);
    }
  };

  const handleSwapLanguages = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
    setSourceText(translatedText);
    setTranslatedText(sourceText);
  };

  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      toast.error("Please enter text to translate");
      return;
    }

    setIsTranslating(true);
    setTranslatedText("");
    setLastResult(null);
    setTranslationStatus("Initializing...");
    setTranslationProgress(10);

    try {
      // Use streaming endpoint for real-time feedback
      const response = await fetch(`${API}/translate/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          text: sourceText,
          source_lang: sourceLang,
          target_lang: targetLang,
          model: selectedModel,
          alert_priority: alertPriority,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedText = '';
      let isCached = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            const eventType = line.substring(7);
            // Update status based on event type
            if (eventType === 'start') {
              setTranslationStatus("Connecting to AI model...");
              setTranslationProgress(20);
            } else if (eventType === 'terminology') {
              setTranslationStatus("Processing terminology...");
              setTranslationProgress(40);
            } else if (eventType === 'model') {
              setTranslationStatus("Generating translation...");
              setTranslationProgress(60);
            } else if (eventType === 'chunk') {
              setTranslationProgress(80);
            } else if (eventType === 'cached') {
              setTranslationStatus("Found in cache!");
              setTranslationProgress(100);
            }
            continue;
          }
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              // Handle different event types
              if (data.cached !== undefined && data.cached === true && !data.translated_text) {
                isCached = true;
              }
              
              if (data.text !== undefined) {
                // Streaming chunk
                if (data.partial) {
                  streamedText += data.text;
                } else {
                  streamedText = data.text; // Full cached text
                }
                setTranslatedText(streamedText);
                setTranslationStatus("Receiving translation...");
              }
              
              if (data.translated_text !== undefined) {
                // Complete event with full result
                setTranslatedText(data.translated_text);
                setLastResult(data);
                setTranslationProgress(100);
                setTranslationStatus("");
                
                if (data.confidence_alert) {
                  toast.warning(data.confidence_alert_message, { duration: 6000 });
                } else {
                  const cacheLabel = data.cached ? ' (cached)' : '';
                  toast.success(`Translated in ${data.processing_time_ms}ms${cacheLabel}`);
                }
              }
              
              if (data.error) {
                toast.error(data.error);
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      console.error("Translation error:", error);
      toast.error(error.message || "Translation failed. Please try again.");
    } finally {
      setIsTranslating(false);
      setTranslationStatus("");
      setTranslationProgress(0);
    }
  };

  // Quick translate using fastest model (Gemini 3 Flash)
  const handleQuickTranslate = async () => {
    if (!sourceText.trim()) {
      toast.error("Please enter text to translate");
      return;
    }

    setIsTranslating(true);
    setTranslatedText("");
    setLastResult(null);
    setTranslationStatus("Quick translating...");
    setTranslationProgress(30);

    try {
      const response = await fetch(`${API}/translate/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          text: sourceText,
          source_lang: sourceLang,
          target_lang: targetLang,
          model: "gemini-3-flash", // Always use fastest model
          alert_priority: alertPriority,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              
              if (data.text !== undefined) {
                if (data.partial) {
                  streamedText += data.text;
                } else {
                  streamedText = data.text;
                }
                setTranslatedText(streamedText);
                setTranslationProgress(70);
              }
              
              if (data.translated_text !== undefined) {
                setTranslatedText(data.translated_text);
                setLastResult(data);
                setTranslationProgress(100);
                
                const cacheLabel = data.cached ? ' (cached)' : '';
                toast.success(`Quick translated in ${data.processing_time_ms}ms${cacheLabel}`, {
                  description: "Using Gemini 3 Flash for speed"
                });
              }
              
              if (data.error) {
                toast.error(data.error);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }
    } catch (error) {
      console.error("Quick translation error:", error);
      toast.error(error.message || "Translation failed. Please try again.");
    } finally {
      setIsTranslating(false);
      setTranslationStatus("");
      setTranslationProgress(0);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(translatedText);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const [isExporting, setIsExporting] = useState(false);
  
  const handleExportPDF = async () => {
    if (!lastResult) return;
    
    setIsExporting(true);
    try {
      const response = await axios.post(
        `${API}/translate/export-pdf`,
        {
          source_text: sourceText,
          translated_text: translatedText,
          source_lang: sourceLang,
          target_lang: targetLang,
          model_used: lastResult.model_used,
          confidence_score: lastResult.confidence_score,
          processing_time_ms: lastResult.processing_time_ms,
          terminology_used: lastResult.terminology_used || [],
          timestamp: lastResult.timestamp,
        },
        {
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MeteoTranslate_Report_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded successfully");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to generate PDF report");
    } finally {
      setIsExporting(false);
    }
  };

  const loadSample = async (sample) => {
    setSourceText(sample.text);
    setTranslatedText("");
    setLastResult(null);
    
    // Auto-translate if using live alerts
    if (useLiveAlerts && sample.text) {
      setIsTranslating(true);
      try {
        // Use streaming for auto-translation too
        const response = await fetch(`${API}/translate/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify({
            text: sample.text,
            source_lang: sourceLang,
            target_lang: targetLang,
            model: selectedModel,
            alert_priority: "high",
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamedText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.substring(6));
                
                if (data.text !== undefined) {
                  if (data.partial) {
                    streamedText += data.text;
                  } else {
                    streamedText = data.text;
                  }
                  setTranslatedText(streamedText);
                }
                
                if (data.translated_text !== undefined) {
                  setTranslatedText(data.translated_text);
                  setLastResult(data);
                  
                  if (data.confidence_alert) {
                    toast.warning(data.confidence_alert_message, { duration: 6000 });
                  } else {
                    const cacheLabel = data.cached ? ' (cached)' : '';
                    toast.success(`Auto-translated live alert in ${data.processing_time_ms}ms${cacheLabel}`, {
                      description: `Model: ${data.model_used} | Confidence: ${(data.confidence_score * 100).toFixed(0)}%`
                    });
                  }
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
        }
      } catch (error) {
        console.error("Auto-translation error:", error);
        toast.error("Auto-translation failed. Click Translate to retry.");
      } finally {
        setIsTranslating(false);
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error("Please select a CSV file");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleBatchUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    setBatchResults(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('source_lang', sourceLang);
      formData.append('target_lang', targetLang);
      formData.append('model', selectedModel);

      const response = await axios.post(`${API}/translate/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setBatchResults(response.data);
      toast.success(`Batch translation complete: ${response.data.total} texts processed`);
      
      if (response.data.low_confidence_count > 0) {
        toast.warning(`${response.data.low_confidence_count} translations flagged for low confidence`);
      }
    } catch (error) {
      console.error("Batch upload error:", error);
      toast.error(error.response?.data?.detail || "Batch translation failed");
    } finally {
      setIsUploading(false);
    }
  };

  const downloadBatchResults = () => {
    if (!batchResults) return;

    const csvContent = [
      ['source_text', 'translated_text', 'confidence_score', 'confidence_alert', 'model_used'].join(','),
      ...batchResults.translations.map(t => [
        `"${t.source_text.replace(/"/g, '""')}"`,
        `"${t.translated_text.replace(/"/g, '""')}"`,
        t.confidence_score,
        t.confidence_alert,
        t.model_used
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch_translations_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.9) return "text-emerald-600";
    if (score >= 0.75) return "text-primary";
    if (score >= 0.6) return "text-secondary";
    return "text-red-500";
  };

  const getConfidenceLabel = (score) => {
    if (score >= 0.9) return "High";
    if (score >= 0.75) return "Good";
    if (score >= 0.6) return "Medium";
    return "Low";
  };

  return (
    <div className="min-h-screen pb-12 bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Globe className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl font-bold tracking-tight text-foreground">MeteoTranslate</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/translate" className="nav-link active text-sm font-medium" data-testid="nav-translate">
              Translate
            </Link>
            <Link to="/terminology" className="nav-link text-sm font-medium" data-testid="nav-terminology">
              Terminology
            </Link>
            <Link to="/metrics" className="nav-link text-sm font-medium" data-testid="nav-metrics">
              Metrics
            </Link>
            <Link to="/analytics" className="nav-link text-sm font-medium" data-testid="nav-analytics">
              Analytics
            </Link>
            <Link to="/history" className="nav-link text-sm font-medium" data-testid="nav-history">
              History
            </Link>
            <Link to="/settings" className="nav-link text-sm font-medium" data-testid="nav-settings">
              Settings
            </Link>
            {isAdmin && (
              <Link to="/rag-admin" className="nav-link text-sm font-medium text-purple-600" data-testid="nav-rag-admin">
                MLOps
              </Link>
            )}
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                <span>{user.name}</span>
              </div>
            )}
            {user && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={async () => {
                  await logout();
                  navigate("/signed-out");
                }}
                className="border-border"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Sign Out
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mb-2 text-foreground" data-testid="page-title">
              Translation Interface
            </h1>
            <p className="text-muted-foreground">
              Translate meteorological content between Canadian English and Canadian French
            </p>
          </div>
          
          {/* Batch Upload Button */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-border" data-testid="batch-upload-btn">
                <Upload className="w-4 h-4 mr-2" />
                Batch Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Batch File Upload</DialogTitle>
                <DialogDescription>
                  Upload a CSV file with a "text" column to translate multiple texts at once.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* File Input */}
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    selectedFile ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".csv"
                    className="hidden"
                  />
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="w-8 h-8 text-primary" />
                      <div className="text-left">
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to select a CSV file or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Max 100 texts per file
                      </p>
                    </>
                  )}
                </div>

                {/* Language Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                      Source Language
                    </label>
                    <select 
                      value={sourceLang} 
                      onChange={(e) => setSourceLang(e.target.value)}
                      className="w-full p-2 border border-border rounded-md bg-white"
                    >
                      <option value="en-ca">EN-CA (English)</option>
                      <option value="fr-ca">FR-CA (French)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                      Target Language
                    </label>
                    <select 
                      value={targetLang} 
                      onChange={(e) => setTargetLang(e.target.value)}
                      className="w-full p-2 border border-border rounded-md bg-white"
                    >
                      <option value="fr-ca">FR-CA (French)</option>
                      <option value="en-ca">EN-CA (English)</option>
                    </select>
                  </div>
                </div>

                {/* Upload Button */}
                <Button 
                  onClick={handleBatchUpload}
                  disabled={!selectedFile || isUploading}
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Translate All
                    </>
                  )}
                </Button>

                {/* Batch Results */}
                {batchResults && (
                  <div className="mt-4 p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Results</span>
                      <Button variant="outline" size="sm" onClick={downloadBatchResults}>
                        Download CSV
                      </Button>
                    </div>
                    <div className="text-sm space-y-1">
                      <p>Total: <span className="font-mono font-bold">{batchResults.total}</span></p>
                      <p>
                        Low Confidence: 
                        <span className={`font-mono font-bold ml-1 ${batchResults.low_confidence_count > 0 ? 'text-secondary' : 'text-emerald-600'}`}>
                          {batchResults.low_confidence_count}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Controls Bar */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted rounded-lg border border-border">
          {/* Model Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              AI Model
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-between bg-white border-border"
                  data-testid="model-selector"
                >
                  {models.find(m => m.id === selectedModel)?.name}
                  <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border-border">
                {models.map((model) => (
                  <DropdownMenuItem
                    key={model.id}
                    onClick={() => setSelectedModel(model.id)}
                    className="cursor-pointer"
                    data-testid={`model-option-${model.id}`}
                  >
                    <div>
                      <div className="font-medium">{model.name}</div>
                      <div className="text-xs text-muted-foreground">{model.description}</div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Priority Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Alert Priority
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-between bg-white border-border"
                  data-testid="priority-selector"
                >
                  <span className={priorities.find(p => p.id === alertPriority)?.color}>
                    {priorities.find(p => p.id === alertPriority)?.name}
                  </span>
                  <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-40 bg-white border-border">
                {priorities.map((priority) => (
                  <DropdownMenuItem
                    key={priority.id}
                    onClick={() => setAlertPriority(priority.id)}
                    className={`cursor-pointer ${priority.color}`}
                    data-testid={`priority-option-${priority.id}`}
                  >
                    {priority.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Sample Alerts */}
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {useLiveAlerts && (
                  <img 
                    src="/eccc-logo.svg" 
                    alt="ECCC" 
                    className="h-5 w-auto"
                    title="Environment and Climate Change Canada"
                  />
                )}
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  {useLiveAlerts ? "Live ECCC Alerts" : "Sample Alerts"}
                </label>
              </div>
              <div className="flex items-center gap-2">
                {useLiveAlerts && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={refreshLiveAlerts}
                    disabled={isRefreshingAlerts}
                    className="h-6 px-2 text-xs"
                    data-testid="refresh-alerts-btn"
                  >
                    {isRefreshingAlerts ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      "Refresh"
                    )}
                  </Button>
                )}
                <button
                  onClick={() => setUseLiveAlerts(!useLiveAlerts)}
                  className={`text-xs px-2 py-1 rounded-full transition-colors ${
                    useLiveAlerts 
                      ? "bg-green-100 text-green-700 border border-green-300" 
                      : "bg-gray-100 text-gray-600 border border-gray-300"
                  }`}
                  data-testid="live-alerts-toggle"
                >
                  {useLiveAlerts ? "🔴 LIVE" : "Sample"}
                </button>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-between bg-white border-border"
                  data-testid="sample-selector"
                >
                  {useLiveAlerts 
                    ? `Select Live Alert (${samples.length})` 
                    : "Select Sample Alert"
                  }
                  <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 max-h-96 overflow-y-auto bg-white border-border">
                {useLiveAlerts && liveAlertsInfo && (
                  <div className={`px-2 py-1.5 text-xs border-b border-border ${liveAlertsInfo.isDemo ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-muted-foreground'}`}>
                    <div className="flex items-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${liveAlertsInfo.isDemo ? 'bg-amber-500' : 'bg-green-500 animate-pulse'}`}></span>
                      {liveAlertsInfo.isDemo ? 'Demo Alerts (no active ECCC warnings)' : 'Live from weather.gc.ca'}
                    </div>
                    <div className="text-[10px] mt-0.5">
                      Updated: {new Date(liveAlertsInfo.lastUpdated).toLocaleTimeString()}
                    </div>
                  </div>
                )}
                {samples.length === 0 ? (
                  <div className="px-2 py-4 text-center text-muted-foreground text-sm">
                    {useLiveAlerts ? "No active alerts from ECCC" : "No samples available"}
                  </div>
                ) : (
                  samples.map((sample, i) => (
                    <DropdownMenuItem
                      key={i}
                      onClick={() => loadSample(sample)}
                      className="cursor-pointer flex-col items-start"
                      data-testid={`sample-option-${i}`}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <span className="font-medium flex-1">{sample.title}</span>
                        {sample.source === "DEMO" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">DEMO</span>
                        )}
                      </div>
                      {useLiveAlerts && (
                        <span className="text-xs text-muted-foreground truncate max-w-full">
                          {sample.text?.substring(0, 60)}...
                        </span>
                      )}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {useLiveAlerts && liveAlertsInfo && (
              <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
                {liveAlertsInfo.isDemo ? (
                  <span className="text-amber-600">Demo mode - ECCC has no active alerts</span>
                ) : (
                  <>
                    <a 
                      href="https://weather.gc.ca" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline text-primary"
                    >
                      Source: weather.gc.ca
                    </a>
                    <span>• Next refresh: {new Date(liveAlertsInfo.nextUpdate).toLocaleTimeString()}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Translation Panels */}
        <div className="grid lg:grid-cols-2 gap-6 relative">
          {/* Source Panel */}
          <div className="translation-panel p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="lang-toggle">
                <button
                  onClick={() => { setSourceLang("en-ca"); setTargetLang("fr-ca"); }}
                  className={`lang-toggle-btn ${sourceLang === "en-ca" ? "active" : ""}`}
                  data-testid="source-lang-en"
                >
                  EN-CA
                </button>
                <button
                  onClick={() => { setSourceLang("fr-ca"); setTargetLang("en-ca"); }}
                  className={`lang-toggle-btn ${sourceLang === "fr-ca" ? "active" : ""}`}
                  data-testid="source-lang-fr"
                >
                  FR-CA
                </button>
              </div>
              <span className="text-xs text-muted-foreground">
                {sourceLang === "en-ca" ? "Canadian English" : "Canadian French"}
              </span>
            </div>
            <Textarea
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              placeholder={sourceLang === "en-ca" 
                ? "Enter weather alert in English..." 
                : "Entrez l'alerte météo en français..."}
              className="min-h-[300px] bg-muted/30 border-0 resize-none focus-visible:ring-0 text-base"
              data-testid="source-textarea"
            />
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <span className="text-xs text-muted-foreground">
                {sourceText.length} characters
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={handleQuickTranslate}
                  disabled={isTranslating || !sourceText.trim()}
                  variant="outline"
                  className="border-border hover:border-primary"
                  data-testid="quick-translate-btn"
                  title="Fast translation using Gemini 3 Flash"
                >
                  <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                  Quick
                </Button>
                <Button
                  onClick={handleTranslate}
                  disabled={isTranslating || !sourceText.trim()}
                  className="bg-primary hover:bg-primary-hover text-white shadow-glow min-w-[140px]"
                  data-testid="translate-btn"
                  title={`High accuracy translation using ${models.find(m => m.id === selectedModel)?.name}`}
                >
                  {isTranslating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {translationStatus || "Translating..."}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Translate
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Progress bar during translation */}
            {isTranslating && (
              <div className="mt-2">
                <Progress value={translationProgress} className="h-1" />
                <p className="text-xs text-muted-foreground mt-1 text-right">
                  {translationStatus}
                </p>
              </div>
            )}
          </div>

          {/* Swap Button (centered between panels on large screens) */}
          <div className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <Button
              variant="outline"
              size="icon"
              onClick={handleSwapLanguages}
              className="swap-btn rounded-full w-12 h-12 bg-white border-border hover:border-primary shadow-card"
              data-testid="swap-languages-btn"
            >
              <ArrowLeftRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Target Panel */}
          <div className="translation-panel p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="lang-toggle">
                <button
                  className={`lang-toggle-btn ${targetLang === "en-ca" ? "active" : ""}`}
                  disabled
                  data-testid="target-lang-en"
                >
                  EN-CA
                </button>
                <button
                  className={`lang-toggle-btn ${targetLang === "fr-ca" ? "active" : ""}`}
                  disabled
                  data-testid="target-lang-fr"
                >
                  FR-CA
                </button>
              </div>
              <div className="flex items-center gap-2">
                {lastResult?.terminology_used?.length > 0 && (
                  <button
                    onClick={() => setShowTermHighlights(!showTermHighlights)}
                    className={`text-xs px-2 py-1 rounded-full transition-colors flex items-center gap-1 ${
                      showTermHighlights 
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-300" 
                        : "bg-gray-100 text-gray-600 border border-gray-300"
                    }`}
                    data-testid="toggle-highlights"
                    title="Toggle terminology highlighting"
                  >
                    <Lock className="w-3 h-3" />
                    {showTermHighlights ? "Highlighting ON" : "Highlighting OFF"}
                  </button>
                )}
                <span className="text-xs text-muted-foreground">
                  {targetLang === "en-ca" ? "Canadian English" : "Canadian French"}
                </span>
              </div>
            </div>
            <div 
              className="min-h-[300px] bg-muted/30 rounded-md p-3 text-base whitespace-pre-wrap leading-relaxed"
              data-testid="target-output"
            >
              {isTranslating && !translatedText ? (
                <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                  <div className="flex items-center gap-3 mb-4">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="text-lg font-medium">{translationStatus || "Processing..."}</span>
                  </div>
                  <Progress value={translationProgress} className="w-48 h-2" />
                  <p className="text-xs mt-2 text-muted-foreground">
                    {translationProgress < 60 ? "First-time translations may take a few seconds" : "Almost there..."}
                  </p>
                </div>
              ) : translatedText ? (
                <>
                  {highlightTerminology(translatedText, lastResult?.terminology_used, false)}
                  {lastResult?.terminology_used?.length > 0 && showTermHighlights && (
                    <div className="mt-4 pt-3 border-t border-border">
                      <div className="flex items-center gap-2 text-xs text-emerald-700 mb-2">
                        <Lock className="w-3 h-3" />
                        <span className="font-medium">
                          {lastResult.terminology_used.length} locked term{lastResult.terminology_used.length > 1 ? 's' : ''} (100% accuracy guaranteed)
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {lastResult.terminology_used.map((term, i) => (
                          <span 
                            key={i}
                            className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded border border-emerald-200"
                          >
                            <span className="text-gray-500">{term.original}</span>
                            <span>→</span>
                            <span className="font-medium">{term.translation}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">
                  {targetLang === "en-ca" 
                    ? "Translation will appear here..." 
                    : "La traduction apparaîtra ici..."}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <span className="text-xs text-muted-foreground">
                {translatedText.length} characters
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleExportPDF}
                  disabled={!lastResult || isExporting}
                  className="border-border"
                  data-testid="export-pdf-btn"
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-4 h-4 mr-2" />
                      Export PDF
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCopy}
                  disabled={!translatedText}
                  className="border-border"
                  data-testid="copy-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-emerald-600" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Confidence Alert */}
        {lastResult?.confidence_alert && (
          <Alert variant="destructive" className="mt-6 border-secondary bg-secondary/10">
            <AlertTriangle className="h-4 w-4 text-secondary" />
            <AlertTitle className="text-secondary">Low Confidence Translation</AlertTitle>
            <AlertDescription className="text-secondary/80">
              {lastResult.confidence_alert_message}
            </AlertDescription>
          </Alert>
        )}

        {/* Translation Details */}
        {lastResult && (
          <div className="mt-8 p-6 bg-white rounded-lg border border-border shadow-card" data-testid="translation-details">
            <h3 className="font-heading text-lg font-bold mb-4 text-foreground">Translation Details</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {/* Confidence Score */}
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Confidence
                </div>
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-20 h-20 -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      fill="none"
                      strokeWidth="6"
                      className="confidence-ring-bg"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="36"
                      fill="none"
                      strokeWidth="6"
                      strokeDasharray={`${lastResult.confidence_score * 226} 226`}
                      className={`confidence-ring-fill ${
                        lastResult.confidence_score >= 0.9 ? "high" : 
                        lastResult.confidence_score >= 0.75 ? "" : 
                        lastResult.confidence_score >= 0.6 ? "medium" : "low"
                      }`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`font-mono text-lg font-bold ${getConfidenceColor(lastResult.confidence_score)}`}>
                      {(lastResult.confidence_score * 100).toFixed(0)}%
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {getConfidenceLabel(lastResult.confidence_score)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Processing Time */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Processing Time
                </div>
                <div className="font-mono text-2xl font-bold text-primary">
                  {lastResult.processing_time_ms}ms
                </div>
                <Progress 
                  value={Math.min((lastResult.processing_time_ms / 1000) * 100, 100)} 
                  className="h-1 mt-2"
                />
              </div>

              {/* Translation Source */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Translation Source
                </div>
                <div className="flex items-center gap-2">
                  {lastResult.translation_source === "TM" ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-sm font-medium">
                      <Database className="w-4 h-4" />
                      Translation Memory
                      {lastResult.tm_match_type && (
                        <span className="text-xs opacity-75">
                          ({lastResult.tm_match_type} match - {(lastResult.tm_confidence * 100).toFixed(0)}%)
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                      <Sparkles className="w-4 h-4" />
                      AI Model
                      {lastResult.tm_context_used && (
                        <span className="text-xs opacity-75">(TM-assisted)</span>
                      )}
                      {lastResult.rag_context_used && (
                        <span className="text-xs opacity-75 ml-1">(RAG-enhanced)</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="font-mono text-xs text-muted-foreground mt-1">
                  {lastResult.model_used}
                </div>
              </div>

              {/* Fuzzy Matches (if any) */}
              {lastResult.fuzzy_matches && lastResult.fuzzy_matches.length > 0 && lastResult.translation_source !== "TM" && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    Similar TM Entries
                  </div>
                  <div className="space-y-1 text-xs">
                    {lastResult.fuzzy_matches.slice(0, 3).map((match, i) => (
                      <div key={i} className="p-2 bg-gray-50 rounded text-gray-600">
                        <div className="flex justify-between">
                          <span className="truncate max-w-[200px]">"{match.matched_text}"</span>
                          <span className="text-emerald-600 font-medium">{(match.confidence * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RAG Semantic Matches (if any) */}
              {lastResult.rag_context_used && lastResult.rag_matches && lastResult.rag_matches.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    <span className="flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      RAG Context (Semantic Search)
                    </span>
                  </div>
                  <div className="space-y-1 text-xs">
                    {lastResult.rag_matches.slice(0, 3).map((match, i) => (
                      <div key={i} className="p-2 bg-purple-50 rounded text-gray-600">
                        <div className="flex justify-between items-start">
                          <span className="truncate max-w-[180px] text-xs">"{match.source_text?.substring(0, 60)}..."</span>
                          <span className="text-purple-600 font-medium ml-2">{(match.similarity * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Terminology */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Locked Terms Used
                </div>
                {lastResult.terminology_used.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {lastResult.terminology_used.map((term, i) => (
                      <span key={i} className="term-badge" data-testid={`term-badge-${i}`}>
                        <Lock className="w-3 h-3" />
                        {term.original}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">No locked terms detected</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile Swap Button */}
        <div className="lg:hidden flex justify-center my-4">
          <Button
            variant="outline"
            onClick={handleSwapLanguages}
            className="swap-btn rounded-full"
            data-testid="swap-languages-btn-mobile"
          >
            <ArrowLeftRight className="w-5 h-5 mr-2" />
            Swap Languages
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TranslatePage;
